import React from 'react';
import { IconButton, Box, Button } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';
import { useNavigate } from 'react-router-dom';

const PAGE_ID = 'm2_u2_x3_verbraucher';

const images = [
  { key: 'verbraucher_fussbodenheizung', src: '/assets/drawable/verbraucher_fussbodenheizung.jpeg', alt: 'Fußbodenheizung', label: 'FBH' },
  { key: 'verbraucher_heizkoerper', src: '/assets/drawable/verbraucher_heizkoerper.jpeg', alt: 'Heizkörper', label: 'Heizkörper' },
  { key: 'verbraucher_deckensegel', src: '/assets/drawable/verbraucher_deckensegel.jpg', alt: 'Deckensegel', label: 'Deckensegel' },
  { key: 'verbraucher_le', src: '/assets/drawable/verbraucher_le.jpg', alt: 'Lufterhitzer', label: 'Lufterhitzer' },
  { key: 'verbraucher_ulkle', src: '/assets/drawable/verbraucher_ulkle.jpg', alt: 'Unterflurkonvektor', label: 'ULKLE' },
  { key: 'verbraucher_unterflurkonvektor', src: '/assets/drawable/verbraucher_unterflurkonvektor.jpg', alt: 'Unterflurkonvektor Detail', label: 'UFK Detail' },
  { key: 'verbraucher_bkt_aufbau', src: '/assets/drawable/verbraucher_bkt_aufbau.jpg', alt: 'BKT Aufbau', label: 'BKT Aufbau' },
  { key: 'verbraucher_bkt_aufbau_verteiler', src: '/assets/drawable/verbraucher_bkt_aufbau_verteiler.jpg', alt: 'BKT Verteiler', label: 'BKT Verteiler' },
  { key: 'verbraucher_bkt_verteiler', src: '/assets/drawable/verbraucher_bkt_verteiler.jpg', alt: 'BKT Verteilerdetail', label: 'BKT Detail' },
  { key: 'verbraucher_wandheizung', src: '/assets/drawable/verbraucher_wandheizung.webp', alt: 'Wandheizung', label: 'Wandheizung' }
];

const textBlocks = [
  {
    id: 'block_einleitung',
    heading: 'Heizverbraucher und die Auswirkungen auf den Heizkreis',
    body:
      'Es gibt eine Vielzahl von Heizverbrauchern, die Wärmeenergie von einem Wärmeerzeuger (z.B. Heizkessel, Wärmepumpe) in einen Raum übertragen. ' +
      'Die Wahl des Systems hängt von vielen Faktoren ab, wie z.B. dem Gebäudetyp, dem gewünschten Komfort, der Energieeffizienz und den Investitionskosten. ' +
      'Jeder Heizverbraucher stellt spezifische Anforderungen an den Heizkreis, insbesondere an die Vorlauftemperatur, die Regelung und die notwendigen Sicherheitskomponenten.'
  },
  {
    id: 'block_fbh',
    heading: 'Fußbodenheizung',
    body:
      'Beschreibung: Bei einer Fußbodenheizung werden Heizrohre (meist aus Kunststoff oder Kupfer) mäander- oder schneckenförmig im Estrich des Fußbodens verlegt. ' +
      'Das durchströmende warme Wasser erwärmt den Estrich, der dann die Wärme gleichmäßig als Strahlungswärme an den Raum abgibt. ' +
      'Dies führt zu einer angenehmen Wärmeverteilung und ermöglicht oft niedrigere Raumtemperaturen bei gleichem Behaglichkeitsempfinden.\n\n' +
      'Vorteile: Hoher Strahlungsanteil, keine sichtbaren Heizkörper, gut für Allergiker (weniger Staubaufwirbelung), energieeffizient durch niedrige Vorlauftemperaturen.\n' +
      'Nachteile: Träge Regelung (lange Aufheiz- und Abkühlzeiten), aufwendigere Installation und Reparatur, Estrich-Trocknungszeiten müssen beachtet werden.'
  },
  {
    id: 'block_fbh_heizkreis',
    heading: 'Fußbodenheizung – Heizkreis & Regelung',
    body:
      'Fußbodenheizungen sind Niedertemperatursysteme. Sie benötigen typischerweise Vorlauftemperaturen zwischen 25°C und 40°C (max. oft 45-50°C laut Normen, abhängig vom Bodenbelag und Estrich).\n\n' +
      'Regelstrategien:\n' +
      '• Witterungsgeführte Vorlauftemperaturregelung ist Standard, oft mit einer sehr flachen Heizkurve (z.B. Steilheit 0,2-0,6).\n' +
      '• Einzelraumregelung über Raumthermostate, die Stellantriebe an den Verteilern des Heizkreises steuern, ist üblich und oft vorgeschrieben. Adaptive (selbstlernende) Thermostate können die Trägheit besser berücksichtigen.\n' +
      '• Bei Wärmepumpenbetrieb ist eine möglichst konstante, niedrige Vorlauftemperatur oft effizienter als eine stark schwankende, witterungsgeführte Temperatur.\n\n' +
      'Sicherheitskomponenten: STB am Heizkreisverteiler oder in der Zuleitung zum Mischer ist essentiell, um eine Überhitzung des Estrichs und des Bodenbelags zu verhindern (typische Auslösetemperatur ca. 50-60°C). ' +
      'Schäden am Boden oder Estrich können sonst die Folge sein. Druckbegrenzungsventil im System.\n\n' +
      'Sonstiges: Der hydraulische Abgleich der einzelnen Heizkreise am Verteiler ist entscheidend.'
  },
  {
    id: 'block_heizkoerper',
    heading: 'Heizkörper (Radiatoren / Konvektoren)',
    body:
      'Beschreibung: Heizkörper sind die klassische Form der Wärmeabgabe.\n' +
      '• Radiatoren (z.B. Plattenheizkörper, Gliederheizkörper) geben Wärme sowohl durch Strahlung als auch durch Konvektion ab.\n' +
      '• Konvektoren haben eine größere Oberfläche durch Lamellen und geben Wärme primär durch Konvektion ab.\n\n' +
      'Vorteile: Relativ schnelle Reaktionszeit, einfache Installation und Austausch, vielfältige Designs.\n' +
      'Nachteile: Können Staub aufwirbeln, benötigen tendenziell höhere Vorlauftemperaturen als Flächenheizungen, können Platz beanspruchen.'
  },
  {
    id: 'block_heizkoerper_heizkreis',
    heading: 'Heizkörper – Heizkreis & Regelung',
    body:
      'Heizkörper benötigen je nach Auslegung und Gebäudestandard höhere Vorlauftemperaturen (z.B. 45°C - 70°C). Moderne Niedertemperatur-Heizkörper können auch mit niedrigeren Temperaturen (z.B. 35-55°C) betrieben werden, erfordern dann aber größere Heizflächen.\n\n' +
      'Regelstrategien:\n' +
      '• Witterungsgeführte Vorlauftemperaturregelung (Heizkurve mit Steilheit z.B. 1,0-1,5).\n' +
      '• Einzelraumregelung erfolgt über Thermostatventile direkt am Heizkörper. Moderne elektronische Thermostatventile bieten programmierbare Zeitprofile und adaptive Regelung.\n\n' +
      'Sicherheitskomponenten: In der Regel keine spezifischen STBs direkt am Heizkörper, da die Systemtemperaturen meist vom Wärmeerzeuger begrenzt werden und die Oberflächentemperaturen bei bestimmungsgemäßem Betrieb unkritisch sind. ' +
      'Der allgemeine STB des Wärmeerzeugers ist hier maßgeblich. Druckbegrenzungsventil im System.\n\n' +
      'Sonstiges: Hydraulischer Abgleich ist sehr wichtig für gleichmäßige Erwärmung und Effizienz.'
  },
  {
    id: 'block_decke',
    heading: 'Deckenheizung / Deckensegel',
    body:
      'Beschreibung: Ähnlich der Fußbodenheizung werden hier Rohre in die Deckenkonstruktion integriert (Nass- oder Trockenbau) oder spezielle Heizelemente (Deckensegel) abgehängt. ' +
      'Die Wärmeabgabe erfolgt primär durch Strahlung von oben. Deckensegel können oft auch zur Kühlung verwendet werden (Klimadecken).\n\n' +
      'Vorteile: Hoher Strahlungsanteil, keine Beeinträchtigung der Wand- und Bodenflächen, schnelle Reaktionszeit (besonders bei Segeln), kann auch kühlen.\n' +
      'Nachteile: Installation kann aufwendiger sein, Gefühl von "heißem Kopf" bei zu hohen Oberflächentemperaturen möglich, erfordert gute Dämmung nach oben.'
  },
  {
    id: 'block_decke_heizkreis',
    heading: 'Deckenheizung – Heizkreis & Regelung',
    body:
      'Die Vorlauftemperaturen sind vergleichbar mit denen von Fußbodenheizungen oder etwas höher, typischerweise 30°C - 50°C. Oberflächentemperaturen der Decke sollten im Komfortbereich bleiben.\n\n' +
      'Regelstrategien:\n' +
      '• Witterungsgeführte Regelung.\n' +
      '• Einzelraumregelung über Raumthermostate.\n' +
      '• Bei kombiniertem Heiz- und Kühlbetrieb ist eine Umschaltung der Regelstrategie und eine Taupunktüberwachung (zur Vermeidung von Kondensation im Kühlbetrieb) notwendig. ' +
      'Die Vorlauftemperatur im Kühlbetrieb muss sicher über dem Taupunkt der Raumluft liegen (z.B. 16-18°C).\n\n' +
      'Sicherheitskomponenten: Ähnlich Fußbodenheizung kann ein STB sinnvoll sein, um Materialschäden oder Unbehagen durch zu hohe Deckentemperaturen zu vermeiden. ' +
      'Bei Kühlbetrieb ist ein Taupunktsensor, der ggf. die Kühlleistung reduziert oder abschaltet, eine wichtige Sicherheits- und Komfortkomponente.'
  },
  {
    id: 'block_lufterhitzer',
    heading: 'Lufterhitzer mit Heizregister (wassergeführt)',
    body:
      'Beschreibung: Lufterhitzer, oft in Industriehallen, Werkstätten oder großen Räumen eingesetzt, bestehen aus einem Ventilator und einem nachgeschalteten Heizregister (Wärmetauscher). ' +
      'Das Heizregister wird mit warmem Wasser aus dem Heizkreis durchströmt. Der Ventilator saugt Raum- oder Außenluft an, führt sie über das warme Register, wo sie erwärmt wird, und bläst sie dann in den Raum.\n\n' +
      'Vorteile: Schnelle Aufheizung großer Luftvolumina, kann mit Lüftungsfunktionen kombiniert werden.\n' +
      'Nachteile: Kann zu Zugerscheinungen führen, Geräuschentwicklung durch Ventilator, Staubaufwirbelung, geringerer Strahlungsanteil, ungleichmäßige Temperaturverteilung möglich.'
  },
  {
    id: 'block_lufterhitzer_heizkreis',
    heading: 'Lufterhitzer – Heizkreis & Regelung',
    body:
      'Lufterhitzer benötigen oft höhere Vorlauftemperaturen (z.B. 60°C - 80°C, teils auch höher) für eine effektive Luftaufheizung, können aber auch für niedrigere Temperaturen ausgelegt werden.\n\n' +
      'Regelstrategien:\n' +
      '• Raumtemperaturregelung über einen Raumthermostat, der den Ventilator (oft mehrstufig) schaltet und/oder ein stetiges (0-10V) oder Auf/Zu-Regelventil im Wasserzulauf zum Heizregister ansteuert.\n' +
      '• Die Vorlauftemperatur des Heizkreises selbst wird meist witterungsgeführt oder auf einen konstanten Wert geregelt.\n\n' +
      'Sicherheitskomponenten: Frostschutzthermostat/-wächter am Heizregister ist zwingend erforderlich, besonders bei Außenluftansaugung oder in frostgefährdeten Bereichen. ' +
      'Dieser stoppt den Ventilator und schließt ggf. die Außenluftklappe und/oder öffnet das Heizventil vollständig, um ein Einfrieren und Platzen des Registers zu verhindern. ' +
      'Überhitzungsschutz (selten, da Luftstrom meist kühlt), aber der STB des Hauptheizkreises ist relevant. Filterüberwachung (Druckdifferenzschalter) um verschmutzte Filter anzuzeigen.'
  },
  {
    id: 'block_ufk',
    heading: 'Unterflurkonvektoren',
    body:
      'Beschreibung: Unterflurkonvektoren sind in den Boden eingelassene Heizsysteme, meist vor großen Fensterflächen. Sie bestehen aus einem Wärmetauscher (Konvektor). ' +
      'Raumluft strömt durch den Schacht, erwärmt sich und steigt auf. Modelle mit natürlicher Konvektion oder mit Gebläseunterstützung (Querstromventilatoren) für höhere Leistung.\n\n' +
      'Vorteile: Unsichtbar, ideal vor bodentiefen Fenstern zur Kaltluftabschirmung, schnelle Reaktionszeit (besonders mit Gebläse).\n' +
      'Nachteile: Können Schmutz ansammeln (Reinigungsaufwand), geringerer Strahlungsanteil, Gebläsemodelle können Geräusche verursachen.'
  },
  {
    id: 'block_ufk_heizkreis',
    heading: 'Unterflurkonvektoren – Heizkreis & Regelung',
    body:
      'Vorlauftemperaturen sind ähnlich denen von Heizkörpern (z.B. 45°C - 70°C).\n\n' +
      'Regelstrategien:\n' +
      '• Raumtemperaturregelung über Raumthermostate, die ein Ventil am Konvektor steuern und/oder das Gebläse (oft mehrstufig oder stufenlos) regeln.\n' +
      '• Die Vorlauftemperatur des Heizkreises wird witterungsgeführt oder konstant gehalten.\n\n' +
      'Sicherheitskomponenten: Bei Modellen mit Gebläse kann eine Überwachung der Ventilatordrehzahl oder ein Strömungswächter sinnvoll sein, um bei Ausfall des Gebläses eine Überhitzung des Konvektors oder des Schachtes zu vermeiden, obwohl dies selten kritisch wird. ' +
      'STB ist in der Regel nicht spezifisch erforderlich, der des Hauptkreises ist relevant.'
  },
  {
    id: 'block_bkt',
    heading: 'Betonkerntemperierung (BKT) / Bauteilaktivierung',
    body:
      'Beschreibung: Rohrsysteme werden direkt in massive Betondecken, -wände oder -böden eingelegt. Diese Bauteile dienen als riesige thermische Speicher und Wärmeübertrager. ' +
      'Wärmeabgabe/-aufnahme erfolgt sehr langsam und gleichmäßig, primär durch Strahlung. BKT wird oft zum Heizen und Kühlen eingesetzt.\n\n' +
      'Vorteile: Sehr energieeffizient durch extrem niedrige Systemtemperaturen (nah an der Raumtemperatur), hoher thermischer Komfort, nutzt Speichermasse, geräuschlos.\n' +
      'Nachteile: Extrem träge Regelung (Stunden bis Tage), eignet sich primär zur Grundlastdeckung, nicht für schnelle Temperaturänderungen, erfordert sorgfältige Planung und Auslegung, kann bei Fehlplanung zu Über- oder Untertemperaturen führen.'
  },
  {
    id: 'block_bkt_heizkreis',
    heading: 'BKT – Heizkreis & Regelung',
    body:
      'BKT-Systeme arbeiten mit sehr niedrigen Vorlauftemperaturen im Heizfall (z.B. 22°C - 28°C) und entsprechend niedrigen Rücklauftemperaturen im Kühlfall (z.B. 18°C - 22°C). Die Spreizung (Differenz Vor-/Rücklauf) ist oft gering (2-5 Kelvin).\n\n' +
      'Regelstrategien:\n' +
      '• Aufgrund der extremen Trägheit ist eine konventionelle Raumtemperaturregelung (direktes Ein/Aus) kaum sinnvoll oder effektiv.\n' +
      '• Oft wird mit konstanter Vorlauftemperatur oder einer sehr flachen, witterungsgeführten Heiz-/Kühlkurve (Heizkurvensteilheit nahe 0) gefahren. Ziel ist die Konditionierung der Bauteilmasse.\n' +
      '• Prädiktive Regelstrategien, die Wettervorhersagen, interne Lasten und Gebäudenutzungsprofile einbeziehen, sind ideal, um die Speichermasse optimal zu nutzen (z.B. Nachtauskühlung im Sommer, Vorheizen bei erwarteter Kälte).\n' +
      '• Begrenzung der maximalen und minimalen Bauteiltemperatur.\n' +
      '• BKT wird oft mit einem schneller reagierenden System (z.B. Heizkörper, Lüftung mit Heiz-/Kühlregister) für die Spitzenlastabdeckung und schnelle Anpassung kombiniert.\n\n' +
      'Sicherheitskomponenten: STB zum Schutz vor zu hohen Bauteiltemperaturen (selten kritisch aufgrund niedriger Systemtemperaturen, aber materialspezifisch zu prüfen). ' +
      'Im Kühlbetrieb ist eine Taupunktüberwachung und -regelung zwingend erforderlich, um Kondensatbildung im Bauteil zu verhindern. Dies kann durch Anhebung der Vorlauftemperatur oder temporäre Abschaltung der Kühlung erfolgen.'
  },
  {
    id: 'block_wand',
    heading: 'Wandheizung',
    body:
      'Beschreibung: Ähnlich wie Fußboden- oder Deckenheizungen werden bei Wandheizungen Rohrsysteme in die Wand integriert (Nass- oder Trockenbausysteme). Die Wärme wird hauptsächlich durch Strahlung an den Raum abgegeben.\n\n' +
      'Vorteile: Hoher Strahlungsanteil, angenehmes Raumklima, unsichtbar, gut für Allergiker, kann auch zur Kühlung verwendet werden.\n' +
      'Nachteile: Wandflächen können nicht beliebig mit Möbeln zugestellt oder für Bohrungen genutzt werden (Planung erforderlich!), Installation kann aufwendiger sein als Heizkörper.'
  },
  {
    id: 'block_wand_heizkreis',
    heading: 'Wandheizung – Heizkreis & Regelung',
    body:
      'Wandheizungen sind ebenfalls Niedertemperatursysteme. Die Vorlauftemperaturen liegen typischerweise zwischen 28°C und 45°C. Die maximale Oberflächentemperatur der Wand sollte den Komfortbereich nicht überschreiten (ca. 35-40°C).\n\n' +
      'Regelstrategien:\n' +
      '• Witterungsgeführte Vorlauftemperaturregelung.\n' +
      '• Einzelraumregelung über Raumthermostate, die Stellantriebe an den Heizkreisverteilern steuern.\n' +
      '• Ähnliche Trägheit wie Fußbodenheizungen, abhängig vom Wandaufbau.\n\n' +
      'Sicherheitskomponenten: Ein STB am Heizkreisverteiler oder in der Zuleitung kann sinnvoll sein, um eine Überhitzung der Wand und mögliche Schäden am Putz oder Wandaufbau zu verhindern. ' +
      'Bei Kühlbetrieb (wenn dafür ausgelegt) ist eine Taupunktüberwachung wichtig.'
  }
];

const M2_U2_X3_Verbraucher: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM2, canUploadImagesM2 } = useAuth();
  const canEditTexts = canEditTextsM2;
  const canUploadImages = canUploadImagesM2;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const blocks = React.useMemo(() => {
    return textBlocks.map(tb => ({
      ...tb,
      body: override?.texts?.[tb.id] ?? tb.body
    }));
  }, [override]);

  const fullText = React.useMemo(() => blocks.map(b => `${b.heading}. ${b.body}`).join(' '), [blocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const imgSrc = (slot: string, fallback: string) => {
    const v = override?.images?.[slot];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const handleTextSave = React.useCallback(async (blockId: string, body: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: body } }, token);
      alert('Text gespeichert.');
      refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = React.useCallback(async (key: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, key, file, token);
      alert('Bild aktualisiert.');
      refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImages]);

  const actions = React.createElement(
    Box,
    { sx: { display: 'flex', alignItems: 'center', gap: 1 } },
    React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Vorlesen' },
      React.createElement(SpeakerIcon, { sx: { fontSize: 28 } })
    ),
    React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m2_u2_x2_hydraulische_schaltungen') }, 'Zurück'),
    React.createElement(Button, { variant: 'contained', onClick: () => navigate('/units/m2_u2_x4_w_w_b') }, 'Weiter')
  );

  return React.createElement(UnitPageShell, { title: '2.2.3 Verbraucher', actions },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 } },
      // Linke Spalte: Bilder
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          alt: img.alt,
          label: 'Bild einfügen',
          onReplace: canUploadImages ? (file: File) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      // Rechte Spalte: Textblöcke
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        blocks.map(tb => React.createElement(EditableTextBlock, {
          key: tb.id,
          pageId: PAGE_ID,
          blockId: tb.id,
          heading: tb.heading,
          body: tb.body,
          onSave: (body: string) => handleTextSave(tb.id, body)
        })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m2_u2_x99_quiz') }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M2_U2_X3_Verbraucher;

